import { useState, useEffect } from 'react';

const VideoPortal = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const VIDEO_ID = 'IS7pCEVfBUg';

  const defaultPlaceholder = 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?auto=format&fit=crop&w=1280&q=80';
  const [thumbnailSrc, setThumbnailSrc] = useState(defaultPlaceholder);

  useEffect(() => {
    if (VIDEO_ID) {
      setThumbnailSrc(`https://img.youtube.com/vi/${VIDEO_ID}/maxresdefault.jpg`);
    } else {
      setThumbnailSrc(defaultPlaceholder);
    }
  }, [VIDEO_ID]);

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative', overflow: 'hidden', borderRadius: '4px' }}>
      {!isPlaying ? (
        /* ── PREVIEW STATE ── */
        <div
          onClick={() => setIsPlaying(true)}
          style={{
            width: '100%',
            height: '100%',
            position: 'relative',
            cursor: 'pointer',
            overflow: 'hidden',
          }}
        >
          {/* Thumbnail */}
          <img
            src={thumbnailSrc}
            onError={() => {
              if (thumbnailSrc.includes('maxresdefault.jpg')) {
                // maxresdefault is not uploaded for this video, fallback to hqdefault (guaranteed to exist)
                setThumbnailSrc(`https://img.youtube.com/vi/${VIDEO_ID}/hqdefault.jpg`);
              } else if (thumbnailSrc.includes('hqdefault.jpg')) {
                // If hqdefault also fails, load premium abstract dark wallpaper placeholder
                setThumbnailSrc(defaultPlaceholder);
              }
            }}
            alt="LRC FLOW Framework"
            width={1280}
            height={720}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
              transition: 'transform 0.6s ease',
            }}
            onMouseOver={e => (e.currentTarget.style.transform = 'scale(1.04)')}
            onMouseOut={e => (e.currentTarget.style.transform = 'scale(1)')}
          />

          {/* Gradient Overlay */}
          <div style={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(to top, rgba(5,5,7,0.92) 0%, rgba(5,5,7,0.45) 50%, rgba(5,5,7,0.15) 100%)',
            pointerEvents: 'none',
          }} />

          {/* Scan-line texture overlay for high-fashion feel */}
          <div style={{
            position: 'absolute',
            inset: 0,
            background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.03) 2px, rgba(0,0,0,0.03) 4px)',
            pointerEvents: 'none',
          }} />

          {/* Centered Play Button */}
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            {/* Pulsing ring */}
            <div style={{
              position: 'absolute',
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              border: '1px solid rgba(162, 204, 169, 0.4)',
              animation: 'pulseRing 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
            }} />
            <div style={{
              position: 'absolute',
              width: '96px',
              height: '96px',
              borderRadius: '50%',
              border: '1px solid rgba(162, 204, 169, 0.2)',
              animation: 'pulseRing 2s cubic-bezier(0.4, 0, 0.6, 1) infinite 0.4s',
            }} />
            {/* Core button */}
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              background: 'rgba(13, 13, 17, 0.7)',
              border: '1.5px solid #a2cca9',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 0 28px rgba(162, 204, 169, 0.55), inset 0 0 12px rgba(162, 204, 169, 0.08)',
              backdropFilter: 'blur(8px)',
              transition: 'all 0.3s ease',
              zIndex: 2,
            }}>
              {/* Play triangle */}
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <path d="M8 5.14v13.72a1 1 0 0 0 1.52.85l11-6.86a1 1 0 0 0 0-1.7l-11-6.86A1 1 0 0 0 8 5.14z" fill="#f4f1ea" />
              </svg>
            </div>
            {/* Click to View Prompt Text */}
            <span style={{
              position: 'absolute',
              top: '90px',
              fontFamily: 'var(--font-mono)',
              fontSize: '0.625rem',
              color: '#f4f1ea',
              letterSpacing: '0.3em',
              textTransform: 'uppercase',
              whiteSpace: 'nowrap',
              background: 'rgba(5, 5, 7, 0.75)',
              padding: '0.4rem 0.9rem',
              borderRadius: '4px',
              border: '1px solid rgba(162, 204, 169, 0.25)',
              pointerEvents: 'none',
              animation: 'pulseText 2s ease-in-out infinite',
              fontWeight: 'bold',
              boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
            }}>
              CLICK TO VIEW
            </span>
          </div>

          {/* Bottom label */}
          <div style={{
            position: 'absolute',
            bottom: '2rem',
            left: '2rem',
            right: '2rem',
          }}>
            <span style={{
              fontFamily: 'var(--font-mono)',
              fontSize: '0.75rem',
              letterSpacing: '0.25em',
              color: '#a2cca9',
              textTransform: 'uppercase',
              display: 'block',
              marginBottom: '0.4rem',
              fontWeight: 600,
            }}>
              LRC FLOW Framework
            </span>
            <h3 style={{
              fontFamily: 'var(--font-serif)',
              fontSize: '1.35rem',
              color: '#f4f1ea',
              margin: 0,
              fontWeight: 400,
            }}>
              Watch the System in Motion
            </h3>
          </div>
        </div>
      ) : (
        /* ── PLAYING STATE ── */
        <iframe
          src={`https://www.youtube.com/embed/${VIDEO_ID}?autoplay=1&rel=0&modestbranding=1&color=white`}
          title="LRC FLOW Framework"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
            display: 'block',
            animation: 'fadeIn 0.3s ease forwards',
          }}
        />
      )}

      <style>{`
        @keyframes pulseRing {
          0% { transform: scale(1); opacity: 0.8; }
          70% { transform: scale(1.5); opacity: 0; }
          100% { transform: scale(1.5); opacity: 0; }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes pulseText {
          0% { opacity: 0.5; transform: scale(0.98); }
          50% { opacity: 1; transform: scale(1.02); }
          100% { opacity: 0.5; transform: scale(0.98); }
        }
      `}</style>
    </div>
  );
};

export default VideoPortal;
